/**
 * Утилита для обработки ошибок API
 */
import i18next from 'i18next';

export interface ApiError {
  code?: string;
  message: string;
  status?: number;
}

/**
 * Конфигурация для retry логики
 */
export interface RetryConfig {
  maxRetries?: number;
  delayMs?: number;
  backoffMultiplier?: number;
  retryableStatusCodes?: number[];
}

/**
 * Ключи локализации для кодов ошибок
 */
const ERROR_MESSAGE_KEYS: Record<string, string> = {
  'LOGIN_ERROR': 'error_login',
  'REGISTRATION_ERROR': 'error_registration',
  'TOKEN_EXPIRED': 'error_token_expired',
  'TOKEN_INVALID': 'error_token_invalid',
  'UNAUTHORIZED': 'error_unauthorized',
  'FORBIDDEN': 'error_forbidden',
  'INVALID_CREDENTIALS': 'error_invalid_credentials',
  'INVALID_EMAIL_OR_PASSWORD': 'error_invalid_credentials',
  'USER_NOT_FOUND': 'error_user_not_found',
  'INVALID_EMAIL': 'error_invalid_email',
  'INVALID_PASSWORD': 'error_invalid_password',
  'EMAIL_ALREADY_EXISTS': 'error_email_already_exists',
  'USER_ALREADY_EXISTS': 'error_email_already_exists',
  'EMAIL_TAKEN': 'error_email_taken',
  'WEAK_PASSWORD': 'error_weak_password',
  'INVALID_NAME': 'error_invalid_name',
  'NAME_TOO_SHORT': 'error_name_too_short',
  'USER_DATA_NOT_AVAILABLE': 'error_user_data_not_available',
  'PROFILE_NOT_FOUND': 'error_profile_not_found',
  'USER_NOT_ACTIVE': 'error_user_not_active',
  'VALIDATION_ERROR': 'error_validation',
  'INVALID_INPUT': 'error_invalid_input',
  'MISSING_REQUIRED_FIELD': 'error_missing_required_field',
  'SERVER_ERROR': 'error_server',
  'NETWORK_ERROR': 'error_network',
  'TIMEOUT': 'error_timeout',
  'DEFAULT': 'error_default',
};

/**
 * Парсит ошибку API и возвращает форматированное сообщение
 */
export function parseApiError(error: any): ApiError {
  // Axios error с ответом сервера
  if (error?.response) {
    const status = error.response.status;
    const data = error.response.data;
    
    // Получаем код ошибки из ответа
    const code = data?.code || data?.error_code || getErrorCodeFromMessage(data?.error);
    
    // Получаем сообщение об ошибке
    const message = getErrorMessage(code, data?.error, status);
    
    return {
      code,
      message,
      status
    };
  }
  
  // Axios error без ответа (network error)
  if (error?.request) {
    return {
      code: 'NETWORK_ERROR',
      message: i18next.t('error_network_message'),
      status: 0
    };
  }
  
  // Обычная ошибка
  if (error?.message) {
    const code = getErrorCodeFromMessage(error.message);
    return {
      code,
      message: getErrorMessage(code, error.message),
      status: error.status
    };
  }
  
  // Неизвестная ошибка
    return {
      code: 'DEFAULT',
      message: i18next.t('error_unknown'),
      status: undefined
    };
}

/**
 * Получает код ошибки из сообщения
 */
function getErrorCodeFromMessage(message?: string): string {
  if (!message) return 'DEFAULT';
  
  const msg = message.toUpperCase();
  
  if (msg.includes('EMAIL') && msg.includes('EXISTS')) return 'EMAIL_ALREADY_EXISTS';
  if (msg.includes('USER') && msg.includes('EXISTS')) return 'USER_ALREADY_EXISTS';
  if (msg.includes('EMAIL_TAKEN')) return 'EMAIL_TAKEN';
  if (msg.includes('INVALID') && (msg.includes('EMAIL') || msg.includes('PASSWORD'))) return 'INVALID_CREDENTIALS';
  if (msg.includes('NOT FOUND') || msg.includes('NOT_FOUND')) return 'USER_NOT_FOUND';
  if (msg.includes('UNAUTHORIZED') || msg.includes('TOKEN')) return 'UNAUTHORIZED';
  if (msg.includes('FORBIDDEN')) return 'FORBIDDEN';
  if (msg.includes('WEAK') && msg.includes('PASSWORD')) return 'WEAK_PASSWORD';
  if (msg.includes('NETWORK')) return 'NETWORK_ERROR';
  if (msg.includes('TIMEOUT')) return 'TIMEOUT';
  
  return 'DEFAULT';
}

/**
 * Возвращает понятное сообщение об ошибке
 */
function getErrorMessage(code: string, originalMessage?: string, status?: number): string {
  // Проверяем точное совпадение кода
  const i18nKey = ERROR_MESSAGE_KEYS[code];
  if (i18nKey) {
    return i18next.t(i18nKey);
  }
  
  // Проверяем статус код
  if (status) {
    switch (status) {
      case 400:
        return originalMessage || i18next.t('error_http_400');
      case 401:
        return i18next.t('error_unauthorized');
      case 403:
        return i18next.t('error_forbidden');
      case 404:
        return i18next.t('error_http_404');
      case 409:
        if (originalMessage?.toLowerCase().includes('email')) {
          return i18next.t('error_email_already_exists');
        }
        return originalMessage || i18next.t('error_http_409');
      case 422:
        return originalMessage || i18next.t('error_http_422');
      case 500:
        return i18next.t('error_server');
      case 502:
        return i18next.t('error_http_502');
      case 503:
        return i18next.t('error_http_502');
      case 504:
        return i18next.t('error_http_504');
    }
  }
  
  if (originalMessage) {
    return originalMessage;
  }
  
  return i18next.t('error_default');
}

/**
 * Проверяет, является ли ошибка ошибкой авторизации
 */
export function isAuthError(error: any): boolean {
  const parsed = parseApiError(error);
  return parsed.status === 401 || 
         parsed.code === 'UNAUTHORIZED' || 
         parsed.code === 'TOKEN_EXPIRED' ||
         parsed.code === 'TOKEN_INVALID';
}

/**
 * Проверяет, является ли ошибка ошибкой "email уже существует"
 */
export function isEmailExistsError(error: any): boolean {
  const parsed = parseApiError(error);
  return parsed.code === 'EMAIL_ALREADY_EXISTS' || 
         parsed.code === 'USER_ALREADY_EXISTS' ||
         parsed.code === 'EMAIL_TAKEN' ||
         parsed.status === 409;
}

/**
 * Проверяет, является ли ошибка ошибкой неверных учётных данных
 */
export function isInvalidCredentialsError(error: any): boolean {
  const parsed = parseApiError(error);
  return parsed.code === 'INVALID_CREDENTIALS' ||
         parsed.code === 'INVALID_EMAIL_OR_PASSWORD' ||
         parsed.code === 'USER_NOT_FOUND' ||
         (parsed.status === 401 && parsed.code !== 'TOKEN_EXPIRED');
}

/**
 * HTTP коды статусов, при которых имеет смысл делать retry
 */
const DEFAULT_RETRYABLE_STATUS_CODES = [408, 429, 500, 502, 503, 504];

/**
 * Выполняет операцию с retry логикой и экспоненциальной задержкой
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  config: RetryConfig = {}
): Promise<T> {
  const {
    maxRetries = 3,
    delayMs = 1000,
    backoffMultiplier = 2,
    retryableStatusCodes = DEFAULT_RETRYABLE_STATUS_CODES,
  } = config;

  let lastError: any;
  let currentDelay = delayMs;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;

      // Проверяем, стоит ли делать retry
      const shouldRetry = shouldRetryError(error, retryableStatusCodes);

      if (!shouldRetry || attempt === maxRetries) {
        // Не делаем retry или это последняя попытка
        break;
      }

      // Логируем retry
      console.warn(`[Retry] Attempt ${attempt}/${maxRetries} failed, retrying in ${currentDelay}ms...`);

      // Ждём перед следующей попыткой
      await sleep(currentDelay);

      // Увеличиваем задержку (экспоненциальная задержка)
      currentDelay = Math.min(currentDelay * backoffMultiplier, 30000);
    }
  }

  throw lastError;
}

/**
 * Проверяет, стоит ли делать retry для данной ошибки
 */
function shouldRetryError(error: any, retryableStatusCodes: number[]): boolean {
  // Network errors
  if (error?.code === 'NETWORK_ERROR' || error?.message?.includes('network')) {
    return true;
  }

  // Timeout errors
  if (error?.code === 'TIMEOUT' || error?.message?.includes('timeout')) {
    return true;
  }

  // HTTP status codes
  const status = error?.status || error?.response?.status;
  if (status && retryableStatusCodes.includes(status)) {
    return true;
  }

  // Не делаем retry для ошибок клиента (4xx кроме 408, 429)
  if (status && status >= 400 && status < 500) {
    return false;
  }

  return false;
}

/**
 * Utility функция для сна
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Circuit Breaker паттерн для предотвращения каскадных сбоев
 */
class CircuitBreaker {
  private state: 'CLOSED' | 'OPEN' | 'HALF_OPEN' = 'CLOSED';
  private failureCount = 0;
  private successCount = 0;
  private lastFailureTime?: Date;

  constructor(
    private failureThreshold: number = 5,
    private successThreshold: number = 2,
    private resetTimeout: number = 30000 // 30 seconds
  ) {}

  async execute<T>(operation: () => Promise<T>): Promise<T> {
    // Проверяем состояние
    if (this.state === 'OPEN') {
      // Проверяем, не пора ли перейти в HALF_OPEN
      if (this.lastFailureTime &&
          Date.now() - this.lastFailureTime.getTime() >= this.resetTimeout) {
        this.state = 'HALF_OPEN';
        console.log('[CircuitBreaker] State: OPEN -> HALF_OPEN');
      } else {
        throw new Error('Circuit breaker is open. Try again later.');
      }
    }

    try {
      const result = await operation();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  private onSuccess() {
    this.failureCount = 0;

    if (this.state === 'HALF_OPEN') {
      this.successCount++;
      if (this.successCount >= this.successThreshold) {
        this.state = 'CLOSED';
        this.successCount = 0;
        console.log('[CircuitBreaker] State: HALF_OPEN -> CLOSED');
      }
    }
  }

  private onFailure() {
    this.failureCount++;
    this.lastFailureTime = new Date();

    if (this.state === 'HALF_OPEN') {
      this.state = 'OPEN';
      console.log('[CircuitBreaker] State: HALF_OPEN -> OPEN');
    } else if (this.failureCount >= this.failureThreshold) {
      this.state = 'OPEN';
      console.log('[CircuitBreaker] State: CLOSED -> OPEN');
    }
  }

  getState(): string {
    return this.state;
  }

  reset() {
    this.state = 'CLOSED';
    this.failureCount = 0;
    this.successCount = 0;
    this.lastFailureTime = undefined;
  }
}

/**
 * Map circuit breakers по именам операций
 */
const circuitBreakers = new Map<string, CircuitBreaker>();

/**
 * Получает или создаёт circuit breaker для операции
 */
export function getCircuitBreaker(
  operationName: string,
  failureThreshold: number = 5,
  successThreshold: number = 2,
  resetTimeout: number = 30000
): CircuitBreaker {
  if (!circuitBreakers.has(operationName)) {
    circuitBreakers.set(
      operationName,
      new CircuitBreaker(failureThreshold, successThreshold, resetTimeout)
    );
  }
  return circuitBreakers.get(operationName)!;
}

/**
 * Выполняет операцию с circuit breaker
 */
export async function executeWithCircuitBreaker<T>(
  operationName: string,
  operation: () => Promise<T>,
  config?: {
    failureThreshold?: number;
    successThreshold?: number;
    resetTimeout?: number;
  }
): Promise<T> {
  const breaker = getCircuitBreaker(
    operationName,
    config?.failureThreshold,
    config?.successThreshold,
    config?.resetTimeout
  );
  return breaker.execute(operation);
}
